import React, { useMemo } from "react";

export type BracketPlayer = { id: string; name: string };

type Style = {
  colW: number; rowH: number; baseGap: number; colGap: number; lineColor: string;
};

const DEFAULT_STYLE: Style = { colW: 260, rowH: 56, baseGap: 16, colGap: 32, lineColor: "#CBD5E1" };

type Result = {
  round: number;        // 1..N
  pos: number;          // 0..(matchesInRound-1)
  winner: 1 | 2;        // 1 = top player, 2 = bottom player
  score?: string;
};

type Props = {
  title?: string;
  /** Input order = seed ranking (1 strongest). BYEs are auto-added/seated properly. */
  players: (BracketPlayer | string)[];
  /** Optional: match results to propagate winners. */
  results?: Result[];
  options?: Partial<Style>;
};

/* ---------- helpers ---------- */
const nextPow2 = (n: number) => { let p = 1; while (p < Math.max(1, n)) p <<= 1; return Math.max(2, p); };
const isBye = (p?: BracketPlayer | { id: string; name: "BYE" } | null) => !!p && p.name.toUpperCase() === "BYE";

/** Balanced seeding order: returns seeds (1..size) in slot order (top→bottom). */
function seedingOrder(size: number): number[] {
  if (size === 1) return [1];
  if (size === 2) return [1, 2];
  const half = size / 2;
  const prev = seedingOrder(half);
  const mirror = prev.map(s => size + 1 - s);
  const out: number[] = [];
  for (let i = 0; i < prev.length; i += 2) out.push(prev[i], mirror[i], mirror[i + 1], prev[i + 1]);
  return out;
}

/* ---------- component ---------- */
export default function AutoBracketWithResults({ title, players: rawPlayers, results = [], options }: Props) {
  const style = { ...DEFAULT_STYLE, ...(options || {}) };

  // Normalize players; input order is seeding rank
  const ranked: BracketPlayer[] = useMemo(() => {
    return rawPlayers.map((p, i) => (typeof p === "string" ? { id: `p${i + 1}`, name: p } : p));
  }, [rawPlayers]);

  // Bracket size & rounds
  const size = useMemo(() => nextPow2(ranked.length), [ranked.length]);
  const rounds = Math.log2(size);

  // Round-1 leaves from true seeding (pad with BYEs)
  const leaves: (BracketPlayer | { id: string; name: "BYE" })[] = useMemo(() => {
    const order = seedingOrder(size); // seeds in slot order
    const filled: (BracketPlayer | { id: string; name: "BYE" })[] = Array(size).fill({ id: "bye", name: "BYE" });
    for (let seed = 1; seed <= size; seed++) {
      const slot = order.indexOf(seed);
      const p = ranked[seed - 1] ?? { id: `bye${seed}`, name: "BYE" as const };
      filled[slot] = p;
    }
    return filled;
  }, [ranked, size]);

  // Fast lookup for results
  const resMap = useMemo(() => {
    const m = new Map<string, Result>();
    for (const r of results) m.set(`${r.round}:${r.pos}`, r);
    return m;
  }, [results]);

  // Build per-round nodes & winner propagation
  type Participant = BracketPlayer | { id: string; name: "BYE" } | null;
  type Node = { a?: Participant; b?: Participant; score?: string; decided?: boolean; winner?: Participant; };
  const nodes: Node[][] = useMemo(() => {
    const N: Node[][] = Array.from({ length: rounds + 1 }, (): Node[] => []);
    // r=0 (leaves) -> pseudo nodes with single player (for convenience)
    for (let i = 0; i < size; i++) N[0][i] = { a: leaves[i] };

    // rounds 1..final
    for (let r = 1; r <= rounds; r++) {
      const count = size >> r;
      for (let i = 0; i < count; i++) {
        // entrants come from previous round winners or (for r=1) directly from leaves
        const childTop  = N[r - 1][2 * i];
        const childBot  = N[r - 1][2 * i + 1];
        const entrantA  = r === 1 ? childTop?.a : childTop?.winner;
        const entrantB  = r === 1 ? childBot?.a : childBot?.winner;

        const node: Node = { a: entrantA ?? null, b: entrantB ?? null };
        // auto-advance if BYE vs player
        if (entrantA && !entrantB) { node.decided = true; node.winner = entrantA; }
        else if (!entrantA && entrantB) { node.decided = true; node.winner = entrantB; }
        else if (entrantA && entrantB) {
          const res = resMap.get(`${r}:${i}`);
          if (res?.winner === 1) { node.decided = true; node.winner = entrantA; node.score = res.score; }
          else if (res?.winner === 2) { node.decided = true; node.winner = entrantB; node.score = res.score; }
        }
        N[r][i] = node;
      }
    }
    return N;
  }, [leaves, rounds, size, resMap]);

  // Geometry
  const centers: number[][] = useMemo(() => {
    const C: number[][] = [];
    C[0] = Array.from({ length: size }, (_, i) => i * (style.rowH + style.baseGap) + style.rowH / 2);
    for (let r = 1; r <= rounds; r++) {
      const count = size >> r;
      C[r] = Array.from({ length: count }, (_, i) => (C[r - 1][2 * i] + C[r - 1][2 * i + 1]) / 2);
    }
    return C;
  }, [size, rounds, style.rowH, style.baseGap]);

  const X = (col: number) => col * (style.colW + style.colGap);
  const totalWidth = X(rounds) + style.colW;
  const totalHeight = centers[0][centers[0].length - 1] + style.rowH / 2;

  // Boxes (leaves)
  const boxes: React.ReactNode[] = [];
  for (let i = 0; i < size; i++) {
    const top = centers[0][i] - style.rowH / 2;
    const p = nodes[0][i].a;
    boxes.push(
      <div key={`leaf-${i}`} style={{ position: "absolute", left: X(0), top, width: style.colW, height: style.rowH }}>
        <div
          style={{
            height: "100%",
            padding: "8px 10px",
            border: "1px solid var(--border-subtle)",
            borderRadius: 10,
            background: "var(--surface-elevated)",
            display: "flex",
            alignItems: "center",
            fontWeight: 800,
            color: isBye(p) ? "var(--text-secondary)" : "inherit",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {p?.name}
        </div>
      </div>
    );
  }

  // Boxes (rounds 1..final) — show entrants, bold winner, include score if present
  for (let r = 1; r <= rounds; r++) {
    const count = size >> r;
    for (let i = 0; i < count; i++) {
      const top = centers[r][i] - style.rowH / 2;
      const n = nodes[r][i];
      const winnerId = (n.winner as any)?.id;
      boxes.push(
        <div key={`r${r}-m${i}`} style={{ position: "absolute", left: X(r), top, width: style.colW, height: style.rowH }}>
          <div
            style={{
              height: "100%",
              padding: "8px 10px",
              border: "1px solid var(--border-subtle)",
              borderRadius: 10,
              background: "var(--surface-elevated)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <div style={{ fontWeight: n.a && (n.a as any).id === winnerId ? 900 : 700, opacity: n.a ? 1 : 0.5 }}>
              {n.a?.name ?? ""}
            </div>
            <div style={{ fontWeight: n.b && (n.b as any).id === winnerId ? 900 : 700, opacity: n.b ? 1 : 0.5 }}>
              {n.b?.name ?? ""}
            </div>
            {!!n.score && <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>Score: {n.score}</div>}
          </div>
        </div>
      );
    }
  }

  // SVG connectors UNDER boxes
  const paths: React.ReactNode[] = [];
  for (let r = 1; r <= rounds; r++) {
    const count = size >> r;
    const xLeft = X(r - 1) + style.colW;
    const xm = xLeft + style.colGap / 2;
    const xRight = X(r);
    const xRightEdge = xRight + style.colW;

    for (let i = 0; i < count; i++) {
      const yTop = centers[r - 1][2 * i];
      const yBot = centers[r - 1][2 * i + 1];
      const yMid = centers[r][i];
      // elbow from children into column r, then across to the far edge (keeps continuity)
      paths.push(<path key={`t-${r}-${i}`} d={`M ${xLeft} ${yTop} H ${xm}`} stroke={style.lineColor} fill="none" strokeWidth={2} />);
      paths.push(<path key={`b-${r}-${i}`} d={`M ${xLeft} ${yBot} H ${xm}`} stroke={style.lineColor} fill="none" strokeWidth={2} />);
      paths.push(<path key={`v-${r}-${i}`} d={`M ${xm} ${yTop} V ${yBot}`} stroke={style.lineColor} fill="none" strokeWidth={2} />);
      paths.push(<path key={`o-${r}-${i}`} d={`M ${xm} ${yMid} H ${xRightEdge}`} stroke={style.lineColor} fill="none" strokeWidth={2} />);
    }
  }

  // Labels
  const labels: React.ReactNode[] = [];
  for (let r = 0; r <= rounds; r++) {
    labels.push(
      <div key={`lbl-${r}`} style={{ position: "absolute", left: X(r), top: -24, color: "var(--text-secondary)", fontWeight: 700, fontSize: 12 }}>
        {r === 0 ? "Round 1" : r === rounds ? "Final" : `Round ${r + 1}`}
      </div>
    );
  }

  return (
    <div style={{ position: "relative", width: totalWidth, height: totalHeight, overflowX: "auto" }}>
      {title && <div style={{ marginBottom: 6, fontWeight: 900 }}>{title}</div>}
      {labels}
      {/* SVG underneath */}
      <svg width={totalWidth} height={totalHeight} style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        {paths}
      </svg>
      {/* Boxes on top */}
      <div style={{ position: "absolute", inset: 0, zIndex: 1 }}>{boxes}</div>
    </div>
  );
}
